import React, { useState, useMemo, useEffect } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Reusable multi-select filter dropdown (checkbox list + search + Select All
 * + Clear/Done). Standardizes every categorical filter across CSM (Task
 * Table, Kanban, Developer/UI-UX Queues, Manager Approval, Social Media,
 * Reports, User Management). Options stay OR'd within one filter; pages
 * combine separate filters with AND.
 *
 * `options` accepts plain strings or `{ value, label, searchText? }` objects.
 */
export const MultiSelectFilter = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  searchable = true,
  selectAll = true,
  placeholder,
  disabled = false,
  loading = false,
  className = '',
  triggerClassName = '',
  renderOption,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const slug = testId || `multiselect-${(label || 'filter').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

  const normalized = useMemo(
    () => options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [options]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return normalized;
    const q = search.trim().toLowerCase();
    return normalized.filter(
      (o) => (o.label || '').toLowerCase().includes(q) || (o.searchText || '').toLowerCase().includes(q)
    );
  }, [normalized, search]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);
  const allSelected = normalized.length > 0 && normalized.every((o) => selectedSet.has(o.value));
  const someSelected = !allSelected && normalized.some((o) => selectedSet.has(o.value));

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const toggleValue = (value) => {
    if (selectedSet.has(value)) onChange(selectedValues.filter((v) => v !== value));
    else onChange([...selectedValues, value]);
  };

  const toggleSelectAll = () => onChange(allSelected ? [] : normalized.map((o) => o.value));
  const clearAll = () => onChange([]);

  const triggerText = () => {
    if (loading) return 'Loading...';
    if (selectedValues.length === 0) return placeholder || `All ${label}`;
    if (selectedValues.length === 1) {
      const match = normalized.find((o) => o.value === selectedValues[0]);
      return match ? match.label : selectedValues[0];
    }
    return `${selectedValues.length} Selected`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || loading}
          data-testid={`${slug}-trigger`}
          className={cn(
            'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background transition-colors focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            selectedValues.length > 0 && 'border-indigo-300 bg-indigo-50/60 text-indigo-700 font-medium',
            triggerClassName
          )}
        >
          <span className="truncate">{triggerText()}</span>
          <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-64 p-0 overflow-hidden', className)} align="start">
        <div className="px-3 pt-3 pb-2">
          <p className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">{label}</p>
        </div>
        {searchable && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="h-8 pl-8 text-sm"
                data-testid={`${slug}-search`}
              />
            </div>
          </div>
        )}
        {selectAll && normalized.length > 1 && (
          <>
            <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 text-sm font-medium text-slate-700">
              <Checkbox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={toggleSelectAll}
                data-testid={`${slug}-select-all`}
              />
              Select All
            </label>
            <div className="h-px bg-slate-100 mx-3" />
          </>
        )}
        <div className="max-h-56 overflow-y-auto py-1">
          {filteredOptions.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-400 text-center">No matches found</p>
          ) : (
            filteredOptions.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 text-sm text-slate-700"
                data-testid={`${slug}-option-${String(opt.value).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <Checkbox checked={selectedSet.has(opt.value)} onCheckedChange={() => toggleValue(opt.value)} />
                {renderOption ? renderOption(opt) : <span className="truncate">{opt.label}</span>}
              </label>
            ))
          )}
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/60">
          <button
            type="button"
            onClick={clearAll}
            disabled={selectedValues.length === 0}
            className="text-xs font-medium text-slate-500 hover:text-red-600 disabled:opacity-40 disabled:hover:text-slate-500"
            data-testid={`${slug}-clear`}
          >
            Clear
          </button>
          <span className="text-xs text-slate-400">
            {selectedValues.length > 0 ? `${selectedValues.length} selected` : ''}
          </span>
          <Button
            type="button"
            size="sm"
            className="h-7 px-3 text-xs"
            onClick={() => setOpen(false)}
            data-testid={`${slug}-done`}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectFilter;
